import { BlogPost } from '../../blog/types';

const post: BlogPost = {
  id: 'the-power-of-verifiable-domains',
  title: 'The Power of Verifiable Domains',
  excerpt:
    'Most AI workflows fail because they stop at instructions. Verifiable domains build the check into the task so errors collapse instead of compounding.',
  category: 'AI & Technology',
  date: '2024-10-15',
  readTime: '6 min read',
  imageUrl: '/images/blog/ai-team.png',
  author: {
    name: 'James Vogel',
    imageUrl: '/images/blog/james.png',
  },
  slug: 'the-power-of-verifiable-domains',
  featured: false,
  content: [
    {
      type: 'impactStatement',
      content:
        "AI workflows don't fail because models ignore instructions; they fail because no one checks if the work actually solved the problem."
    },
    {
      type: 'paragraph',
      content:
        "I've built dozens of AI workflows, and most fail the same way. You hand over clear instructions, the system produces something, and somehow it's still not the right thing."
    },
    {
      type: 'quote',
      content: 'Classic forest-for-the-trees problem.'
    },
    {
      type: 'paragraph',
      content:
        "It feels familiar because people fall into the same trap. You assume the task is clear, they assume finishing the checklist is success, and the actual problem stays unsolved."
    },
    {
      type: 'divider'
    },
    {
      type: 'heading2',
      content: 'A Human Reminder',
      description: 'Verification is the difference between task completion and problem solving.',
      id: 'human-reminder'
    },
    {
      type: 'paragraph',
      content:
        "Same thing happens with people. I once delegated a bug fix on login—users couldn't log in and were seeing a confusing error message. I told another dev, \"Fix the login message.\" They did exactly that. Clean, helpful message. But users still couldn't log in. They followed my instructions perfectly and missed the entire point."
    },
    {
      type: 'impactStatement',
      content: 'Instructions without verification are just wishes.'
    },
    {
      type: 'divider'
    },
    {
      type: 'heading2',
      content: 'What I Figured Out',
      description: 'Multi-step, verifiable flows beat one-shot systems every time.',
      id: 'what-i-figured-out'
    },
    {
      type: 'paragraph',
      content:
        "I've built both one-shot LLM systems and multi-step flows that verify and validate responses. The difference is night and day."
    },
    {
      type: 'paragraph',
      content:
        'The multi-step approach gives much better results because you can encode success criteria directly into the evaluation prompt.'
    },
    {
      type: 'markdown',
      content:
        `**One-shot RAG:** Ships the first thing the model says. Great for demos, terrible for reliability.\n\n**Verifiable flow:** Generates an answer, validates it against the success criteria, then loops until it passes. That second pass asks, "Does this response actually solve the problem?"`
    },
    {
      type: 'quote',
      content: "You can't just tell someone (or something) what to do. You also need to tell them how to verify they did it right."
    },
    {
      type: 'divider'
    },
    {
      type: 'heading2',
      content: 'Defining Verifiable Domains',
      description: 'Instructions paired with success criteria.',
      id: 'defining-verifiable-domains'
    },
    {
      type: 'paragraph',
      content:
        'Verifiable domains are instructions that come with their own success criteria. When the check is built in, everyone knows what good looks like before the work starts.'
    },
    {
      type: 'heading3',
      content: 'A Real Example',
      id: 'real-example'
    },
    {
      type: 'paragraph',
      content:
        'I ask AI to help me put together a software dev plan based on my research. It always adds timelines and goes into too much detail—gets off the rails on complex tasks. But if I then ask it to verify that the plan avoids timelines and focuses on clean, implementable steps, it cleans itself up.'
    },
    {
      type: 'impactStatement',
      content: 'Research → answer → verify → loop as needed. The verification step keeps the work on the rails.'
    },
    {
      type: 'divider'
    },
    {
      type: 'heading2',
      content: 'Why This Matters',
      description: 'Translation loss happens at every handoff.',
      id: 'why-this-matters'
    },
    {
      type: 'paragraph',
      content:
        "There's always translation loss. Your brain to their brain. Your prompt to the model's interpretation. Something gets lost."
    },
    {
      type: 'paragraph',
      content:
        'But if you also give them a way to check their own work, the success rate jumps. Instead of blindly trusting the output, they confirm it hit the requirements.'
    },
    {
      type: 'markdown',
      content:
        `Instead of:\n\n"Research our competitors"\n\nTry:\n\n"Research our competitors. Your response should include at least 5 direct competitors, their key differentiators, and recent funding. Then verify: If I'm pitching to investors, does this response tell me what companies they'll compare us to and why we're different?"`
    },
    {
      type: 'divider'
    },
    {
      type: 'heading2',
      content: 'This Works Everywhere',
      description: 'Any workflow improves when it verifies its own outputs.',
      id: 'this-works-everywhere'
    },
    {
      type: 'featureList',
      content: [
        {
          title: 'Code reviews',
          description: "Don't just review the diff—pair it with tests that prove it works."
        },
        {
          title: 'AI prompts',
          description: 'Add self-verification steps so the model confirms it met the brief.'
        },
        {
          title: 'Project handoffs',
          description: 'Include success criteria anyone can check, even if they were not in the room.'
        },
        {
          title: 'Team management',
          description: 'Give people a plan for how to know they actually succeeded.'
        }
      ]
    },
    {
      type: 'paragraph',
      content:
        'I learned this by building systems that failed, then fixing them. Once we started measuring results, the pattern was obvious.'
    },
    {
      type: 'paragraph',
      content:
        "We all know LLMs hallucinate and people miss details. Verification flips the script: instead of errors stacking up, each pass catches what the previous one missed. Single pass might give you 90% accuracy. With verification passes, you're targeting the errors so they shrink each round."
    },
    {
      type: 'quote',
      content: "As we delegate more to AI, verification stops being optional. It's workflow engineering, not magic."
    },
    {
      type: 'impactStatement',
      content:
        "Don't just tell people what to do. Tell them how to prove they did it right. The difference in success rates isn't even close."
    }
  ]
};

export default post;
